// Company branding settings. Drives the PDF cover page + accreditations
// strip + warranty page + acceptance footer. Logo uploads stored in GridFS.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Buildings, FloppyDisk, UploadSimple, X, Palette, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { devWarn } from "../lib/log";

const empty = {
  name: "", tagline: "", address: "", phone: "", email: "", website: "",
  registered_no: "", vat_no: "",
  public_liability_insurer: "", public_liability_no: "",
  accreditations: [],
  primary_color: "#1E40AF", accent_color: "#D94A31",
  workmanship_warranty_years: 10, warranty_text: "", terms_text: "",
  default_deposit_pct: 25,
  auto_chase_enabled: true,
  auto_chase_sent_days: 3,
  auto_chase_accepted_days: 7,
  auto_chase_review_days: 14,
};

const PRESET_ACCREDITATIONS = ["NFRC", "TrustMark", "CompetentRoofer", "CHAS", "FairTrades", "Which? Trusted Trader", "RoofCERT", "FMB"];

export default function Settings() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [logoFileId, setLogoFileId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company");
        if (data && data.name) {
          setForm({ ...empty, ...data, accreditations: data.accreditations || [] });
          setLogoFileId(data.logo_file_id || null);
        }
      } catch (e) {
        // First-run: no company doc yet — log silently for diagnostics.
        if (e?.response?.status && e.response.status !== 404) {
          devWarn("Failed to load company branding:", e?.message || e);
        }
      }
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setN = (k) => (e) => setForm({ ...form, [k]: e.target.value === "" ? "" : Number(e.target.value) });

  const toggleAccred = (a) => {
    const list = form.accreditations || [];
    setForm({ ...form, accreditations: list.includes(a) ? list.filter((x) => x !== a) : [...list, a] });
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be ≤ 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/company/logo`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setLogoFileId(data.file_id);
      toast.success("Logo uploaded — will appear on next PDF");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        workmanship_warranty_years: Number(form.workmanship_warranty_years) || 10,
        default_deposit_pct: Number(form.default_deposit_pct) || 25,
        auto_chase_enabled: !!form.auto_chase_enabled,
        auto_chase_sent_days: Number(form.auto_chase_sent_days) || 3,
        auto_chase_accepted_days: Number(form.auto_chase_accepted_days) || 7,
        auto_chase_review_days: Number(form.auto_chase_review_days) || 14,
      };
      const { data } = await api.put("/company", payload);
      setForm({ ...empty, ...data, accreditations: data.accreditations || [] });
      toast.success("Branding saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const logoUrl = logoFileId ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${logoFileId}` : null;

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="flex items-center gap-3 mb-8">
        <Buildings size={28} weight="fill" className="text-[var(--rqp-orange)]" />
        <div>
          <span className="hero-rule" />
          <div className="overline">Branding</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Company &amp; PDF branding</h1>
          <p className="text-sm text-slate-500 mt-1">Your logo, colours, accreditations and warranties — automatically applied to every PDF quote and the customer viewer.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Logo + identity */}
        <section className="lg:col-span-2 card-elev p-6">
          <div className="overline">Identity</div>
          <h2 className="font-display text-2xl font-bold mt-1">Logo &amp; company details</h2>

          <div className="mt-5 flex items-center gap-4 flex-wrap">
            <div
              className="w-40 h-24 bg-slate-900 rounded-sm grid place-items-center overflow-hidden border border-slate-700"
              data-testid="settings-logo-preview"
            >
              {logoUrl ? <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-slate-500 text-xs uppercase tracking-wider">No logo</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onLogo} className="hidden" data-testid="settings-logo-input" />
            <button onClick={() => fileRef.current?.click()} className="btn-secondary flex items-center gap-2" data-testid="settings-logo-upload">
              <UploadSimple size={14} weight="bold" /> {logoUrl ? "Replace logo" : "Upload logo"}
            </button>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">PNG / JPG / SVG · max 2 MB · transparent background recommended</span>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <label className="block sm:col-span-2"><span className="overline">Company name</span><input className="rqp mt-1.5" value={form.name} onChange={set("name")} data-testid="settings-company-name" /></label>
            <label className="block sm:col-span-2"><span className="overline">Tagline</span><input className="rqp mt-1.5" value={form.tagline} onChange={set("tagline")} placeholder="Specialist UK Roofing" /></label>
            <label className="block"><span className="overline">Phone</span><input className="rqp mt-1.5" value={form.phone} onChange={set("phone")} /></label>
            <label className="block"><span className="overline">Email</span><input className="rqp mt-1.5" value={form.email} onChange={set("email")} /></label>
            <label className="block"><span className="overline">Website</span><input className="rqp mt-1.5" value={form.website} onChange={set("website")} /></label>
            <label className="block"><span className="overline">Registered No.</span><input className="rqp mt-1.5" value={form.registered_no} onChange={set("registered_no")} /></label>
            <label className="block"><span className="overline">VAT No.</span><input className="rqp mt-1.5" value={form.vat_no} onChange={set("vat_no")} /></label>
            <label className="block col-span-2"><span className="overline">Registered address</span><textarea rows={2} className="rqp mt-1.5" value={form.address} onChange={set("address")} /></label>
          </div>
        </section>

        {/* Brand colours */}
        <section className="card-elev p-6">
          <div className="flex items-center gap-2">
            <Palette size={18} weight="fill" className="text-[var(--rqp-orange)]" />
            <h2 className="font-display text-xl font-bold">Brand colours</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">Used on the PDF cover band, accreditations strip and total badge.</p>

          <label className="block mt-5">
            <span className="overline">Primary</span>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={form.primary_color || "#1E40AF"} onChange={set("primary_color")} className="w-12 h-10 cursor-pointer rounded-sm border border-slate-300" data-testid="settings-primary-color" />
              <input className="rqp font-mono-data text-sm" value={form.primary_color || ""} onChange={set("primary_color")} />
            </div>
          </label>
          <label className="block mt-3">
            <span className="overline">Accent</span>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={form.accent_color || "#D94A31"} onChange={set("accent_color")} className="w-12 h-10 cursor-pointer rounded-sm border border-slate-300" data-testid="settings-accent-color" />
              <input className="rqp font-mono-data text-sm" value={form.accent_color || ""} onChange={set("accent_color")} />
            </div>
          </label>

          <div className="mt-5 p-3 rounded-sm text-white text-xs font-display font-bold" style={{ background: form.primary_color || "#1E40AF" }} data-testid="settings-color-preview-primary">PRIMARY</div>
          <div className="mt-2 p-3 rounded-sm text-white text-xs font-display font-bold" style={{ background: form.accent_color || "#D94A31" }} data-testid="settings-color-preview-accent">ACCENT</div>
        </section>

        {/* Accreditations */}
        <section className="lg:col-span-2 card-elev p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" className="text-[var(--rqp-blue)]" />
            <h2 className="font-display text-xl font-bold">Accreditations</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">Printed on the PDF cover and customer viewer. Toggle the ones you hold.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {PRESET_ACCREDITATIONS.map((a) => {
              const active = (form.accreditations || []).includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAccred(a)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border ${active ? "bg-[var(--rqp-blue)] text-white border-[var(--rqp-blue)]" : "bg-white text-slate-600 border-slate-300 hover:border-[var(--rqp-blue)]"}`}
                  data-testid={`accreditation-${a}`}
                >
                  {a}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <label className="block"><span className="overline">Public liability insurer</span><input className="rqp mt-1.5" value={form.public_liability_insurer} onChange={set("public_liability_insurer")} /></label>
            <label className="block"><span className="overline">PL policy no.</span><input className="rqp mt-1.5" value={form.public_liability_no} onChange={set("public_liability_no")} /></label>
          </div>
        </section>

        {/* Warranty + Terms */}
        <section className="card-elev p-6">
          <div className="overline">Warranty</div>
          <h2 className="font-display text-xl font-bold mt-1">Workmanship guarantee</h2>
          <label className="block mt-4"><span className="overline">Years</span><input type="number" className="rqp mt-1.5" value={form.workmanship_warranty_years} onChange={setN("workmanship_warranty_years")} /></label>
          <label className="block mt-3"><span className="overline">Default deposit %</span><input type="number" min="0" max="100" className="rqp mt-1.5" value={form.default_deposit_pct} onChange={setN("default_deposit_pct")} /></label>
        </section>

        {/* Auto-chase / CRM automation */}
        <section className="lg:col-span-3 card-elev p-6" data-testid="settings-auto-chase">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="overline">CRM automation</div>
              <h2 className="font-display text-xl font-bold mt-1">Auto-chase follow-ups</h2>
              <p className="text-xs text-slate-500 mt-1">
                Automatically schedule a chase reminder when a quote moves to <strong>Sent</strong> or <strong>Accepted</strong> (no deposit yet), plus a review request once a job is <strong>Completed</strong>. Toggle off if you prefer to schedule reminders manually.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-auto-chase-toggle">
              <input
                type="checkbox"
                checked={!!form.auto_chase_enabled}
                onChange={(e) => setForm({ ...form, auto_chase_enabled: e.target.checked })}
                className="w-4 h-4 accent-[var(--rqp-orange)]"
              />
              <span className="text-xs uppercase tracking-wider font-bold">
                {form.auto_chase_enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <label className="block">
              <span className="overline">Chase Sent quotes after</span>
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="number" min="1" max="60"
                  className="rqp"
                  value={form.auto_chase_sent_days ?? 3}
                  onChange={setN("auto_chase_sent_days")}
                  disabled={!form.auto_chase_enabled}
                  data-testid="settings-auto-chase-sent-days"
                />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </label>
            <label className="block">
              <span className="overline">Chase Accepted (unpaid) after</span>
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="number" min="1" max="60"
                  className="rqp"
                  value={form.auto_chase_accepted_days ?? 7}
                  onChange={setN("auto_chase_accepted_days")}
                  disabled={!form.auto_chase_enabled}
                  data-testid="settings-auto-chase-accepted-days"
                />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </label>
            <label className="block">
              <span className="overline">Ask for review after Completed</span>
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="number" min="1" max="180"
                  className="rqp"
                  value={form.auto_chase_review_days ?? 14}
                  onChange={setN("auto_chase_review_days")}
                  disabled={!form.auto_chase_enabled}
                  data-testid="settings-auto-chase-review-days"
                />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </label>
          </div>
        </section>

        <section className="lg:col-span-3 card-elev p-6">
          <div className="overline">Warranty &amp; terms text</div>
          <p className="text-xs text-slate-500 mt-1">Long-form text printed on the warranty page of each PDF. Leave blank to use the default template.</p>
          <label className="block mt-4">
            <span className="overline">Warranty text</span>
            <textarea rows={4} className="rqp mt-1.5" value={form.warranty_text} onChange={set("warranty_text")} placeholder="All workmanship is guaranteed for X years..." />
          </label>
          <label className="block mt-3">
            <span className="overline">Terms &amp; conditions</span>
            <textarea rows={5} className="rqp mt-1.5" value={form.terms_text} onChange={set("terms_text")} placeholder="Payment terms, cancellation, materials retention..." />
          </label>
        </section>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="settings-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save branding"}
        </button>
      </div>
    </div>
  );
}
